import type {
  Cart as V3Cart,
  CartProduct as V3CartProduct,
  CartCrmRedemption as V3CrmRedemption,
} from '@iheartjane/data-access';

import type {
  Cart as V1Cart,
  PendingCartProduct as V1CartProduct,
  PosCart as V1PosCart,
} from '@jane/shared/types';

// Derived from the v1 types, since not all nested shapes are exported.
type V1BrandDiscount = V1Cart['brand_discounts'][number];
type V1CrmRedemption = NonNullable<V1Cart['crm_redemptions']>[number];
type V1UnavailableProduct = V1Cart['unavailable_products'][number];
type V1Inventory = NonNullable<V1CartProduct['inventories']>[number];
type V1Photo = NonNullable<V1CartProduct['photos']>[number];
type V1ProductEffect = NonNullable<V1CartProduct['effects']>[number];
type V1ProductFlavor = NonNullable<V1CartProduct['flavors']>[number];
type V3ProductEffect = V3CartProduct['effects'][number];
type V3ProductFlavor = V3CartProduct['flavors'][number];
// Not exported from data-access.
type V3PosCartTotals = NonNullable<V3Cart['pos_cart']['totals']>;

/**
 * The API sends plural `apply_to_non_cannabis_items`; the v1 types' singular
 * key doesn't exist. Optional for older cached payloads.
 */
type V1InventoryInput = Omit<V1Inventory, 'tax'> & {
  tax?: {
    apply_to_discounted_price: boolean;
    apply_to_non_cannabis_items?: boolean;
    is_taxable: boolean;
    rate: number;
    title: string;
  } | null;
};

/** The API uses the image URL as the photo `id`, not a number. */
type V1PhotoInput = Omit<V1Photo, 'id'> & { id: number | string };

/** Both APIs send `bypass_redemption`; the v1 type is missing it. */
type V1CrmRedemptionInput = V1CrmRedemption & { bypass_redemption?: boolean };

/** No employee: both APIs send `{ name: null, phone: null }`, never `null`. */
type V1CartEmployee = { name: string | null; phone: string | null };

/**
 * Adds fields the v1 cart API sends but the v1 type lacks. The cart API sends
 * `effects`/`flavors` as `{ id, menu_product_id, kind }`; the v1 type's
 * `{ id, title, value }` is the product API's shape, so both are accepted.
 */
type V1CartProductInput = Omit<
  V1CartProduct,
  'effects' | 'flavors' | 'inventories' | 'photos'
> & {
  brand_discounted_price?: number | null;
  effects?: readonly (V1ProductEffect | V3ProductEffect)[];
  flavors?: readonly (V1ProductFlavor | V3ProductFlavor)[];
  inventories?: readonly V1InventoryInput[];
  kind_subtype?: string | null;
  max_cart_quantity_each?: number | null;
  max_cart_quantity_eighth_ounce?: number | null;
  max_cart_quantity_gram?: number | null;
  max_cart_quantity_half_gram?: number | null;
  max_cart_quantity_half_ounce?: number | null;
  max_cart_quantity_ounce?: number | null;
  max_cart_quantity_quarter_ounce?: number | null;
  max_cart_quantity_two_gram?: number | null;
  photos?: readonly V1PhotoInput[];
};

/**
 * The slice's cart at runtime: the `@jane/shared/models` shape (wider ids, plus
 * `amounts_manager`), modeled here because that package can't be imported.
 * Timestamps are epoch seconds in both APIs, so no conversion.
 */
export type V1CartInput = Omit<
  V1Cart,
  | 'cart_discount_special_id'
  | 'crm_redemptions'
  | 'group_special_id'
  | 'id'
  | 'products'
  | 'removed_crm_redemptions'
  | 'store'
> & {
  amount?: string | null;
  amounts_manager?: string;
  cart_discount_special_id?: string | number | null;
  checked_out_at_with_store_timezone?: number | null;
  created_at?: number | null;
  crm_redemptions?: V1CrmRedemptionInput[];
  dismissed_message?: string | null;
  employee?: V1CartEmployee;
  fee_tax_amount?: number;
  group_special_id?: string | number | null;
  id?: string | number | null;
  product_counts_have_been_capped?: boolean;
  products: V1CartProductInput[];
  promotions_opt_out?: boolean;
  rating?: number | null;
  removed_crm_redemptions?: V1CrmRedemptionInput[];
  status?: string;
  store?: { id: string | number } | null;
};

// Pure v1 → v3 cart mapper (ADR-0001).
// TODO(carts-v3): remove once v3 is the only source.

/** v1 money strings → v3 `number | null` ('' and non-numeric → null). */
const toNumberOrNull = (value: string | null | undefined): number | null => {
  if (value == null) return null;
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** v1 epoch string → v3 `number | null`. A date string → `null`, not 2026. */
const toEpochOrNull = (value: string | null | undefined): number | null => {
  if (value == null || !/^-?\d+(\.\d+)?$/.test(value)) return null;
  return Number(value);
};

/** v1 `PosCartTotals` → v3: every key present, missing ones `null`. */
const mapPosCartTotals = (
  totals: V1PosCart['totals'] | undefined
): V3PosCartTotals | null =>
  totals == null
    ? null
    : {
        delivery_fee: totals.delivery_fee ?? null,
        rounding_amount: totals.rounding_amount ?? null,
        sales_tax: totals.sales_tax ?? null,
        service_fee: totals.service_fee ?? null,
        store_tax: totals.store_tax ?? null,
        total: totals.total ?? null,
      };

/** v1 `tip_amount` string → v3 required `number` (non-numeric → 0). */
const toNumber = (value: string | null | undefined): number => {
  const parsed = Number.parseFloat(value ?? '');
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** v1 ids are `string | number` at runtime → v3 `number | null`. */
const toNumberIdOrNull = (
  value: string | number | null | undefined
): number | null => (value == null ? null : Number(value));

const mapInventory = (
  inventory: V1InventoryInput
): V3CartProduct['inventories'][number] => ({
  bulk: inventory.bulk,
  equivalency: inventory.equivalency,
  id: inventory.id,
  menu_product_id: inventory.menu_product_id,
  price_id: inventory.price_id,
  tax: inventory.tax
    ? {
        apply_to_discounted_price: inventory.tax.apply_to_discounted_price,
        apply_to_non_cannabis_items:
          inventory.tax.apply_to_non_cannabis_items ?? false,
        is_taxable: inventory.tax.is_taxable,
        rate: inventory.tax.rate,
        title: inventory.tax.title,
      }
    : null,
});

/** Keeps only cart-API entries, which have `kind`. */
const cartWireEntries = <T extends { kind: unknown }>(
  entries: readonly unknown[] | undefined
): T[] =>
  (entries ?? []).filter(
    (entry): entry is T =>
      typeof entry === 'object' && entry !== null && 'kind' in entry
  );

const mapPhoto = (photo: V1PhotoInput): V3CartProduct['photos'][number] => ({
  id: String(photo.id),
  position: photo.position,
  urls: photo.urls,
});

const mapCrmRedemption = (
  redemption: V1CrmRedemptionInput
): V3CrmRedemption => ({
  bypass_redemption: redemption.bypass_redemption ?? false,
  cart_uuid: redemption.cart_uuid,
  crm_provider: redemption.crm_provider,
  crm_redemption_id: redemption.crm_redemption_id ?? null,
  crm_reward_id: String(redemption.crm_reward_id),
  id: String(redemption.id ?? ''),
  point_value: redemption.point_value,
  redeemed: redemption.redeemed ?? false,
  redemption_failed: redemption.redemption_failed ?? null,
  reward: {
    amount: redemption.reward.amount ?? 0,
    type: redemption.reward.type ?? '',
  },
  reward_name: redemption.reward_name,
});

/** v1 `PendingCartProduct` → v3 `CartProduct`. Fallbacks cover old caches. */
export const mapV1CartProductToV3 = (
  product: V1CartProductInput
): V3CartProduct => ({
  aggregate_rating: product.aggregate_rating ?? null,
  allow_multiple_flower_count: product.allow_multiple_flower_count,
  amount: product.amount ?? null,
  applicable_brand_special_ids: product.applicable_brand_special_ids ?? [],
  brand: product.brand ?? null,
  brand_discount_label: product.brand_discount_label ?? null,
  brand_discount_title: product.brand_discount_title ?? null,
  // Same value v3 sends, so pass it through rather than recompute.
  brand_discounted_price: product.brand_discounted_price ?? null,
  brand_special_has_segment_rules:
    product.brand_special_has_segment_rules ?? false,
  brand_special_id: product.brand_special_id ?? null,
  brand_subtype: product.brand_subtype,
  category: product.category,
  cbd_dosage_milligrams: product.cbd_dosage_milligrams ?? 0,
  count: product.count,
  description: product.description,
  discounted_price_each: toNumberOrNull(product.discounted_price_each),
  discounted_price_eighth_ounce: toNumberOrNull(
    product.discounted_price_eighth_ounce
  ),
  discounted_price_gram: toNumberOrNull(product.discounted_price_gram),
  discounted_price_half_gram: toNumberOrNull(
    product.discounted_price_half_gram
  ),
  discounted_price_half_ounce: toNumberOrNull(
    product.discounted_price_half_ounce
  ),
  discounted_price_ounce: toNumberOrNull(product.discounted_price_ounce),
  discounted_price_quarter_ounce: toNumberOrNull(
    product.discounted_price_quarter_ounce
  ),
  discounted_price_two_gram: toNumberOrNull(product.discounted_price_two_gram),
  effects: cartWireEntries<V3ProductEffect>(product.effects),
  flavors: cartWireEntries<V3ProductFlavor>(product.flavors),
  id: product.id,
  image_urls: product.image_urls ?? [],
  inventories: (product.inventories ?? []).map(mapInventory),
  kind: product.kind,
  kind_subtype: product.kind_subtype ?? null,
  max_cart_quantity: product.max_cart_quantity,
  max_cart_quantity_each: product.max_cart_quantity_each ?? null,
  max_cart_quantity_eighth_ounce:
    product.max_cart_quantity_eighth_ounce ?? null,
  max_cart_quantity_gram: product.max_cart_quantity_gram ?? null,
  max_cart_quantity_half_gram: product.max_cart_quantity_half_gram ?? null,
  max_cart_quantity_half_ounce: product.max_cart_quantity_half_ounce ?? null,
  max_cart_quantity_ounce: product.max_cart_quantity_ounce ?? null,
  max_cart_quantity_quarter_ounce:
    product.max_cart_quantity_quarter_ounce ?? null,
  max_cart_quantity_two_gram: product.max_cart_quantity_two_gram ?? null,
  name: product.name,
  net_weight_grams: product.net_weight_grams,
  operator_store_rank: product.operator_store_rank ?? null,
  percent_cbd: product.percent_cbd,
  percent_thc: product.percent_thc,
  photos: (product.photos ?? []).map(mapPhoto),
  price_each: toNumberOrNull(product.price_each),
  price_eighth_ounce: toNumberOrNull(product.price_eighth_ounce),
  price_gram: toNumberOrNull(product.price_gram),
  price_half_gram: toNumberOrNull(product.price_half_gram),
  price_half_ounce: toNumberOrNull(product.price_half_ounce),
  price_id: product.price_id,
  price_ounce: toNumberOrNull(product.price_ounce),
  price_quarter_ounce: toNumberOrNull(product.price_quarter_ounce),
  price_two_gram: toNumberOrNull(product.price_two_gram),
  product_brand_id: product.product_brand_id ?? null,
  product_id: product.product_id,
  quantity_units: product.quantity_units ?? null,
  quantity_value: product.quantity_value ?? null,
  review_count: product.review_count ?? 0,
  root_subtype: product.root_subtype ?? null,
  special_amount: toNumberOrNull(product.special_amount),
  special_id: product.special_id ?? null,
  special_title: product.special_title ?? null,
  store_notes: product.store_notes ?? null,
  thc_dosage_milligrams: product.thc_dosage_milligrams ?? 0,
  type: product.type,
  url_slug: product.url_slug ?? null,
  visible: product.visible,
});

const mapBrandDiscount = (
  discount: V1BrandDiscount
): V3Cart['brand_discounts'][number] => ({
  brand_special_id: discount.brand_special_id,
  discount_total_amount: discount.discount_total_amount,
  discount_total_with_fee: discount.discount_total_with_fee,
  product_brand: discount.product_brand,
  special_title: discount.special_title,
  special_type: discount.special_type,
});

const mapUnavailableProduct = (
  product: V1UnavailableProduct
): V3Cart['unavailable_products'][number] => ({
  name: product.name,
  price_id: product.price_id,
  product_id: product.product_id,
});

/**
 * v1 slice cart → v3 `Cart`. Drops `payment_method`, `tags` and `message`.
 * Any constant field (e.g. `applied_specials`) must be in
 * `RECONCILIATION_ALLOWLIST`, or the reconciliation test fails.
 */
export const mapV1CartToV3 = (
  cart: V1CartInput,
  posCartTotals?: V1PosCart['totals']
): V3Cart => {
  const products = cart.products.map(mapV1CartProductToV3);
  return {
    all_discounts_total: cart.all_discounts_total ?? 0,
    amount: cart.amount ?? null,
    amounts_manager: cart.amounts_manager ?? '',
    // Prefer the flat name; fall back to the app_mode object's name.
    app_mode_name: cart.app_mode_name ?? cart.app_mode?.name ?? '',
    applied_specials: [],
    brand_discounts: cart.brand_discounts.map(mapBrandDiscount),
    brand_discounts_service_fee_total: cart.brand_discounts_service_fee_total,
    brand_discounts_total: cart.brand_discounts_total ?? 0,
    brand_discounts_total_with_fee: cart.brand_discounts_total_with_fee,
    brand_special_has_changed: cart.brand_special_has_changed,
    brand_special_unavailable_reason:
      cart.brand_special_unavailable_reason ?? null,
    cart_discount_amount: cart.cart_discount_amount ?? 0,
    cart_discount_special_id: toNumberIdOrNull(cart.cart_discount_special_id),
    checked_out_at_with_store_timezone:
      cart.checked_out_at_with_store_timezone ?? null,
    created_at: cart.created_at ?? null,
    crm_redemptions: (cart.crm_redemptions ?? []).map(mapCrmRedemption),
    delivery_fee_amount: cart.delivery_fee_amount ?? 0,
    delivery_finished_at: toEpochOrNull(cart.delivery_finished_at),
    delivery_started_at: toEpochOrNull(cart.delivery_started_at),
    discounted_product_total: cart.discounted_product_total ?? 0,
    discounted_subtotal: cart.discounted_subtotal ?? 0,
    dismissed_message: cart.dismissed_message ?? null,
    employee: cart.employee ?? { name: null, phone: null },
    fee_tax_amount: cart.fee_tax_amount ?? 0,
    group_discounts_total: cart.group_discounts_total ?? 0,
    group_special_id: toNumberIdOrNull(cart.group_special_id),
    group_special_title: cart.group_special_title ?? null,
    id: toNumberIdOrNull(cart.id) ?? 0,
    item_total: cart.item_total ?? 0,
    jane_pay_service_fee_amount: cart.jane_pay_service_fee_amount,
    loyalty_points_stacking_enabled: cart.loyalty_points_stacking_enabled,
    max_specials_per_cart: cart.max_specials_per_cart,
    max_specials_per_cart_hit: cart.max_specials_per_cart_hit,
    order_total: cart.order_total ?? 0,
    pos_cart: { totals: mapPosCartTotals(posCartTotals) },
    // Only the Roots partner API computes this; both browser APIs send `false`.
    product_counts_have_been_capped:
      cart.product_counts_have_been_capped ?? false,
    product_discounts_total: cart.product_discounts_total ?? 0,
    products,
    products_count: products.length,
    promo_code: cart.promo_code ?? null,
    promotions_opt_out: cart.promotions_opt_out ?? false,
    quantity_adjusted_products: cart.quantity_adjusted_products ?? [],
    rating: cart.rating ?? null,
    removed_crm_redemptions: (cart.removed_crm_redemptions ?? []).map(
      mapCrmRedemption
    ),
    reservation_mode: cart.reservation_mode,
    rounding_amount: cart.rounding_amount,
    sales_tax_amount: cart.sales_tax_amount,
    sales_tax_rate: cart.sales_tax_rate,
    service_fee_amount: cart.service_fee_amount ?? 0,
    special_has_changed: cart.special_has_changed,
    status: cart.status ?? '',
    // v1 omits `store` before one is set; v3 requires it, so use `{ id: 0 }`.
    store: cart.store ? { id: Number(cart.store.id) } : { id: 0 },
    store_tax_amount: cart.store_tax_amount ?? 0,
    suggested_specials: [],
    tax_included: cart.tax_included,
    tip_amount: toNumber(cart.tip_amount),
    unavailable_products: cart.unavailable_products.map(mapUnavailableProduct),
    uuid: cart.uuid,
  };
};
